<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout justify-space-between>
        <v-flex xs3 sm3 md3 d-flex>
          <div>
            <slot name="header_actions"></slot>
          </div>
        </v-flex>
        <v-flex xs3 sm3 md3 d-flex>
          <v-text-field v-model="filters.search"
                        append-icon="search"
                        :label="trans('app.search.search')"
                        single-line
                        hide-details
                        solo
                        clearable
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>
    <v-data-table
        :disable-initial-sort="true"
        :headers="headers"
        :items="items"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
        :no-data-text="trans('app.search.no_data')"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td>
            {{ props.item.last_name }}
          </td>
          <td>
            {{ props.item.first_name }}
          </td>
          <td>
            {{ `app.` + props.item.sex|trans }}
          </td>
          <td>
            {{ props.item.age }}
          </td>
          <td>
            {{ props.item.email }}
          </td>
          <td>
            {{ props.item.phone }}
          </td>
          <td>
            {{ props.item.created_at|format_date(FORMAT_DATETIME) }}
          </td>
          <td class="justify-end layout px-0 pr-4">
            <v-layout align-center justify-end>
              <icon-button
                  icon="person"
                  color="success"
                  cls="mr-0 ml-0"
                  :permission="hasPermission('patient_show')"
                  :href="`/patients/${props.item.id}/panel`"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.panel' | trans }}
              </icon-button>
              <icon-button
                  icon="date_range"
                  color="purple"
                  cls="mr-0 ml-0"
                  :permission="hasPermission('patient_show')"
                  :href="`/patients/${props.item.id}/menu`"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.menus' | trans }}
              </icon-button>
              <icon-button
                  icon="edit"
                  color="success"
                  cls="mr-0 ml-0"
                  :permission="hasPermission('patient_edit')"
                  @click="editItem(props.item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.edit' | trans }}
              </icon-button>
              <icon-button
                  @click="toggleActive(props.item)"
                  :icon="props.item.is_active ? 'cloud_queue' : 'cloud'"
                  color="amber"
                  cls="mr-0 ml-0"
                  :permission="hasPermission('patient_edit')"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ props.item.is_active ? 'app.add_to_archive' : 'app.remove_from_archive' | trans }}
              </icon-button>
              <icon-button
                  icon="delete"
                  color="red"
                  cls="mr-0 ml-0"
                  :permission="hasPermission('patient_delete')"
                  @click="deleteItem(props.item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.delete' | trans }}
              </icon-button>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
    <div class="text-xs-center pt-2 pb-4">
      <v-pagination v-model="pagination.page" total-visible=10 :length="pages"></v-pagination>
    </div>
  </div>
</template>
<script>
import {stringify} from "qs";
import {trans} from "../../app";
import {mapGetters, mapState} from "vuex";
import debounce from 'lodash/debounce';
import {FORMAT_DATETIME} from "../../utils/dateFormatter";
import PatientService from "../../services/PatientService";

export default {
  props: {
    group: String,
  },
  data: () => ({
    FORMAT_DATETIME: FORMAT_DATETIME,
    filters: {
      search: null,
    },
    pagination: {
      rowsPerPage: 50,
      sortBy: 'last_name',
      descending: true,
    },
    total: 0,
    items: [],
    isLoading: true,
    pages: 0,
    headers: [
      {
        text: trans('app.surname'), align: 'left', value: 'last_name', sortable: true,
      },
      {
        text: trans('app.first_name'), align: 'left', value: 'first_name', sortable: true,
      },
      {
        text: trans('app.sex'), align: 'left', value: 'sex', sortable: true,
      },
      {
        text: trans('app.age'), align: 'left', value: 'age', sortable: true,
      },
      {
        text: trans('app.email'), align: 'left', value: 'email', sortable: true,
      },
      {
        text: trans('app.phone_number'), align: 'left', value: 'phone', sortable: true,
      },
      {
        text: trans('app.createdAt'), align: 'left', value: 'created_at', sortable: true,
      },
      {
        text: '', align: 'center', sortable: false,
      },
    ]
  }),
  computed: {
    ...mapState(['dictionaries']),
    ...mapGetters(['hasPermission']),
  },
  watch: {
    filters: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    pagination: {
      handler(newValue, oldValue) {
        if (oldValue.page !== undefined) {
          this.fetchData();
        }
      },
      deep: true,
    },
  },
  created() {
    this.debounceFetch = debounce(this.fetchData, 300);
  },
  methods: {
    resetSearch() {
      this.filters = {
        search: null
      };
      this.pagination.sortBy = 'last_name';
      this.pagination.descending = false;
    },
    async fetchData() {
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;

      let query = {
        is_active: this.group !== 'all' ? (this.group === 'active') : null,
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
        ...this.filters,
      };

      const response = await axios.get(`/patients/data?${stringify(query, {skipNulls: true})}`);

      const {data, meta} = response.data;
      this.items = data;
      this.total = meta.total;
      this.pages = meta.last_page;
      this.isLoading = false;
    },
    async toggleActive(item) {
      const response = await axios.patch(`/patients/${item.id}/toggle_active`);
      const previousIsActive = item.is_active;
      item.is_active = response.data.is_active;
      await this.fetchData();
      this.$toast(trans(previousIsActive ? 'app.patient_archived' : 'app.patient_activated' ));
    },
    async deleteItem(item) {
      const confirm = await this.$confirm(trans('app.confirm_patient_removal', {name: `${item.last_name} ${item.first_name}`}), {
        title: trans('app.remove_patient')
      });

      if (confirm) {
        await new PatientService().delete(item.id);
        await this.fetchData();
        this.$toast(trans('app.actions.removed', {name: trans('app.patient_accusative')}));
      }
    },
    async editItem(item) {
      this.$emit('edit-patient', {id:item.id});
    }
  }
}
</script>
